import {
  Bitmap,
  Color,
  GlowFilter,
  MovieClip,
  Sprite,
  TextField,
  TextFormat,
} from "../runtime";
import type { BitmapData } from "../runtime";
import { CutSceneSignal, CutSceneStepAction } from "./actions";
import {
  BG_endingCity,
  BG_endingSunset,
  BG_grassyPlains,
  BG_openingCity,
  BG_openingCrenson,
  G_bgCircuit,
  G_circuitBack,
  G_fortressCutScene,
  G_fortressCutScene2,
  G_openingTunnel,
  G_robot,
  G_robotInner,
  blackBlock2,
  endingExplode,
  pPose1,
  pPoseArmour1,
  pPoseArmourSunset1,
  pPoseArmourSunset2,
  pPoseSunset1,
  pPoseSunset2,
  whiteBlock,
} from "./assets";
import {
  CUT_SCENES,
  CUT_SCENE_DEFAULTS,
  CUT_SCENE_STEPS,
} from "./data/cutScenes";
import { Effect } from "./Effect";
import { EffectId } from "./ids";
import { Rain } from "./Rain";
import { FONT_BODY, TEXT_COLOURS, createTextField } from "./textStyle";

/** A scripted step with every default filled in, as the scene plays it. */
interface CutSceneState {
  readonly txt: string;
  readonly frameLimit: number;
  readonly action: CutSceneStepAction;
}

/** Pulse of the ending explosion, indexed by its 1-4 frame counter. */
const EXPLODE_PULSE_SCALE = [0, 3.5, 3.46, 3.42, 3.46] as const;

export class CutScene extends MovieClip {
  private readonly sceneType: number;
  private readonly hyperArmour: boolean;
  private readonly ev: CutSceneState[] = [];
  private readonly format = new TextFormat();
  private readonly txt: TextField;
  private readonly spaceTxt: TextField;
  private readonly tBox: Sprite;
  private readonly sceneCover: MovieClip;
  private readonly effects: Effect[] = [];
  private frame = 0;
  private txtArray: string[] = [];
  private txtCurrent = "";
  private life = 0;
  private fortress!: MovieClip;
  private fortressOn = false;
  private fortressSpeed = 0;
  private fortressRising = false;
  private fortressAlpha = 0;
  private robot!: MovieClip;
  private robotOn = 0;
  private robotSpeed = 0;
  private coverAlpha = 100;
  private coverOn = true;
  private background1!: MovieClip;
  private background2!: MovieClip;
  private background1Scroll = 1;
  private background2Scroll = 2;
  private bg2Alpha = 0;
  private bg2On = false;
  private obj1: MovieClip | null = null;
  private obj1On = false;
  private obj1Alpha = 0;
  private obj1Counter = 0;
  private obj2!: MovieClip;
  private obj2On = false;
  private obj2Alpha = 0;
  private obj1GlowCounter = 0;
  private obj1GlowRising = true;
  private crenson!: MovieClip;
  private crensonAlpha = 0;
  private crensonOn = false;
  private screenFlashOn = false;
  private screenFlashAlpha = 0;
  private screenFlashCounter = 0;
  private screenFlashLimit = 100;
  private screenFlash!: MovieClip;
  private spacePressed = false;
  private spaceAlpha = 0;
  private spaceFading = false;
  private robotShadow = 0;
  private robotScale = 1000;
  private rain: Rain | null = null;
  private rainOn = false;
  private explodeCounter = 0;
  private explodeOn = false;

  constructor(sceneType = 0, hyperArmour = false) {
    super();
    this.sceneType = sceneType;
    this.hyperArmour = hyperArmour;
    this.format.align = "right";
    this.format.font = FONT_BODY;
    this.format.color = TEXT_COLOURS.yellow;
    this.format.size = 16;
    this.spaceTxt = createTextField(this, this.format, {
      width: 300,
      height: 30,
      x: 380,
      y: 12,
      text: "Press SPACE to skip",
    });
    this.spaceTxt.alpha = 0;
    this.format.align = "left";
    this.format.color = TEXT_COLOURS.white;
    this.format.size = 20;
    this.format.leading = -4;
    this.txt = createTextField(this, this.format, {
      width: 630,
      height: 110,
      x: 45,
      y: 363,
      text: "",
    });
    const script = CUT_SCENES[sceneType] ?? [];
    for (let stepIndex = 0; stepIndex < CUT_SCENE_STEPS; stepIndex++) {
      const step = script[stepIndex] ?? {};
      this.ev.push({
        txt: step.txt ?? CUT_SCENE_DEFAULTS.txt,
        frameLimit: step.frameLimit ?? CUT_SCENE_DEFAULTS.frameLimit,
        action: step.action ?? CUT_SCENE_DEFAULTS.action,
      });
    }
    if (sceneType === 0) {
      this.setupOpening();
    }
    if (sceneType === 1) {
      this.setupEnding();
    }
    this.sceneCover = new MovieClip();
    this.addChild(this.sceneCover);
    fillLayer(this.sceneCover, new blackBlock2(), 720, 480);
    this.tBox = new Sprite();
    this.tBox.graphics.beginBitmapFill(new blackBlock2());
    this.tBox.graphics.drawRect(0, 355, 720, 125);
    this.tBox.graphics.endFill();
    this.addChild(this.tBox);
    this.addChild(this.txt);
    this.addChild(this.spaceTxt);
  }

  screenFlashSetup(): void {
    this.screenFlash = new MovieClip();
    this.addChild(this.screenFlash);
    fillLayer(this.screenFlash, new whiteBlock(), 720, 480);
    this.screenFlash.alpha = 0;
  }

  update(spaceDown: boolean): CutSceneSignal {
    ++this.life;
    if (this.spacePressed) {
      return CutSceneSignal.None;
    }
    let returnAction = CutSceneSignal.None;
    if (this.sceneType === 0) {
      this.tickOpening();
    }
    if (this.rain != null && this.rainOn) {
      if (this.rain.updateIntro()) {
        this.removeChild(this.rain);
        this.rain = null;
      }
    }
    if (this.sceneType === 1) {
      this.tickEnding();
    }
    if (this.bg2On) {
      if (this.bg2Alpha < 100) {
        ++this.bg2Alpha;
        this.background2.alpha = this.bg2Alpha * 0.01;
      }
    } else if (this.bg2Alpha > 0) {
      --this.bg2Alpha;
      this.background2.alpha = this.bg2Alpha * 0.01;
    }
    if (this.coverOn) {
      if (this.coverAlpha < 100) {
        this.coverAlpha += 2;
        this.sceneCover.alpha = this.coverAlpha * 0.01;
      }
    } else if (this.coverAlpha > 0) {
      this.coverAlpha -= 2;
      this.sceneCover.alpha = this.coverAlpha * 0.01;
    }
    if (this.screenFlashOn) {
      ++this.screenFlashCounter;
      if (this.screenFlashCounter === this.screenFlashLimit) {
        this.screenFlashLimit = Math.round(Math.random() * 70) + 110;
        this.screenFlashAlpha = 65;
        this.screenFlashCounter = 0;
        returnAction = CutSceneSignal.ThunderFlash;
      }
    }
    if (this.screenFlashAlpha > 0) {
      if (this.sceneType === 0) {
        this.screenFlashAlpha -= 5;
      } else {
        --this.screenFlashAlpha;
      }
      this.screenFlash.alpha = this.screenFlashAlpha * 0.01;
    }
    if (this.txtArray.length > 0) {
      this.addText();
      this.addText();
    }
    this.effectCycle(0);
    ++this.frame;
    if (this.frame === this.ev[0].frameLimit) {
      returnAction = this.nextStep(returnAction);
    }
    if (this.spaceFading) {
      if (this.spaceAlpha > 25) {
        --this.spaceAlpha;
      } else {
        this.spaceFading = false;
      }
    } else if (this.spaceAlpha < 85) {
      ++this.spaceAlpha;
    } else {
      this.spaceFading = true;
    }
    this.spaceTxt.alpha = this.spaceAlpha * 0.01;
    if (spaceDown && this.life > 51) {
      this.spacePressed = true;
      this.spaceTxt.alpha = 1;
      if (this.sceneType === 0) {
        returnAction = CutSceneSignal.OpeningSkipped;
      }
      if (this.sceneType === 1) {
        returnAction = CutSceneSignal.EndingSkipped;
      }
    }
    if (this.explodeOn) {
      ++this.explodeCounter;
      if (this.explodeCounter === 55) {
        const randomExplodeAngle = Math.floor(Math.random() * 360);
        const randomExplodeDist = Math.floor(Math.random() * 70);
        const radExplode = randomExplodeAngle * (Math.PI / 180);
        this.addEffect(
          new Effect(
            EffectId.FortressExplosion,
            randomExplodeDist * Math.cos(radExplode) + this.fortress.x,
            randomExplodeDist * Math.sin(radExplode) + this.fortress.y,
          ),
        );
        this.explodeCounter -= 10;
      }
    }
    return returnAction;
  }

  private setupOpening(): void {
    this.background1 = new MovieClip();
    this.addChild(this.background1);
    fillLayer(this.background1, new BG_grassyPlains(), 2000, 480);
    this.background2 = new MovieClip();
    this.addChild(this.background2);
    fillLayer(this.background2, new BG_openingCity(), 2000, 480);
    this.background2.alpha = 0;
    this.background1.y = -70;
    this.screenFlashSetup();
    this.rain = new Rain(true);
    this.addChild(this.rain);
    this.fortress = new MovieClip();
    this.addChild(this.fortress);
    this.fortress.addChild(centred(new G_fortressCutScene()));
    this.fortress.alpha = 0;
    this.fortress.x = 360;
    this.fortress.y = 145;
    this.crenson = new MovieClip();
    this.crenson.x = 360;
    this.crenson.y = 160;
    this.addChild(this.crenson);
    this.crenson.addChild(centred(new BG_openingCrenson()));
    this.crenson.blendMode = "darken";
    this.crenson.visible = false;
  }

  private setupEnding(): void {
    this.explodeOn = true;
    this.background1 = new MovieClip();
    this.addChild(this.background1);
    fillLayer(this.background1, new BG_endingCity(), 2000, 512);
    this.background1.y = -60;
    this.background1.x = -600;
    this.fortress = new MovieClip();
    this.addChild(this.fortress);
    this.fortress.addChild(centred(new G_fortressCutScene2()));
    this.fortress.x = 345;
    this.fortress.y = 100;
    this.obj1 = new MovieClip();
    this.addChild(this.obj1);
    const explodeSpr = centred(new endingExplode());
    explodeSpr.smoothing = true;
    this.obj1.addChild(explodeSpr);
    this.obj1.visible = false;
    this.obj1.blendMode = "add";
    this.screenFlashSetup();
    this.robot = new MovieClip();
    this.robot.x = 740;
    this.robot.y = 180;
    const robotSpr = new Bitmap(
      this.hyperArmour ? new pPoseArmourSunset1() : new pPoseSunset1(),
    );
    robotSpr.y = robotSpr.height * -0.5;
    this.addChild(this.robot);
    this.robot.addChild(robotSpr);
  }

  private tickOpening(): void {
    if (this.crensonOn) {
      if (this.crensonAlpha < 100) {
        ++this.crensonAlpha;
        this.crenson.alpha = this.crensonAlpha * 0.01;
      }
    } else if (this.crensonAlpha > 0) {
      this.crensonAlpha -= 2;
      this.crenson.alpha = this.crensonAlpha * 0.01;
      if (this.crensonAlpha === 0) {
        this.removeChild(this.crenson);
      }
    }
    if (this.background1Scroll !== 0) {
      this.background1.x += this.background1Scroll;
      if (this.background1Scroll > 0) {
        if (this.background1.x >= 0) {
          this.background1.x -= 1024;
        }
      } else if (this.background1.x < -1000) {
        this.background1.x += 1024;
      }
    }
    if (this.background2Scroll !== 0) {
      this.background2.x += this.background2Scroll;
      if (this.background2.x >= 0) {
        this.background2.x -= 1024;
      }
    }
    if (this.obj1On) {
      if (this.obj1Alpha < 100) {
        this.obj1Alpha += 2;
        this.obj1!.alpha = this.obj1Alpha * 0.01;
      }
    } else if (this.obj1Alpha > 0) {
      this.obj1Alpha -= 2;
      this.obj1!.alpha = this.obj1Alpha * 0.01;
    }
    if (this.obj2On) {
      if (this.obj2Alpha < 100) {
        this.obj2Alpha += 2;
        this.obj2.alpha = this.obj2Alpha * 0.01;
      }
    } else if (this.obj2Alpha > 0) {
      this.obj2Alpha -= 2;
      this.obj2.alpha = this.obj2Alpha * 0.01;
    }
    if (this.fortressOn) {
      if (this.fortressAlpha < 100) {
        ++this.fortressAlpha;
        this.fortress.alpha = this.fortressAlpha * 0.01;
      }
      this.fortress.y += this.fortressSpeed * 0.005;
      if (this.fortressRising) {
        --this.fortressSpeed;
        if (this.fortressSpeed <= -42) {
          this.fortressRising = false;
        }
      } else {
        ++this.fortressSpeed;
        if (this.fortressSpeed > 50) {
          this.fortressRising = true;
        }
      }
    } else if (this.fortressAlpha > 0) {
      this.fortressAlpha -= 2;
      this.fortress.alpha = this.fortressAlpha * 0.01;
      if (this.fortressAlpha === 0) {
        this.fortress.visible = false;
      }
    }
    if (this.obj1 != null) {
      if (this.obj1GlowRising) {
        this.obj1GlowCounter += 5;
        if (this.obj1GlowCounter >= 150) {
          this.obj1GlowRising = false;
        }
      } else {
        this.obj1GlowCounter -= 5;
        if (this.obj1GlowCounter <= 50) {
          this.obj1GlowRising = true;
        }
      }
      const glow = this.obj1GlowCounter * 0.18 + 0.85;
      this.obj1.filters = [new GlowFilter(0, 1, glow, glow, 6, 1)];
    }
    if (this.robotOn > 0) {
      this.robotSpeed += (0 - this.robotSpeed) * 0.04;
      this.robot.x += this.robotSpeed;
    }
  }

  private tickEnding(): void {
    if (this.robotOn === 0) {
      this.background1.x += (-100 - this.background1.x) * 0.005;
      this.fortress.x -= 0.2;
      this.fortress.y += 0.14;
      this.robot.x += (315 - this.robot.x) * 0.005;
      if (this.robotShadow > 0) {
        --this.robotShadow;
      }
      const c = new Color();
      c.setTint(0, this.robotShadow * 0.01);
      this.robot.transform.colorTransform = c;
      if (this.obj1On) {
        const obj1 = this.obj1!;
        obj1.x = this.fortress.x;
        obj1.y = this.fortress.y;
        ++this.obj1Counter;
        obj1.rotation += 0.065;
        const obj1Scale = EXPLODE_PULSE_SCALE[this.obj1Counter];
        if (this.obj1Counter === 4) {
          this.obj1Counter = 0;
        }
        obj1.scaleX = obj1Scale;
        obj1.scaleY = obj1Scale;
      }
    }
    if (this.robotOn === 1) {
      this.robotScale += (100 - this.robotScale) * 0.0011;
      const robotScaleFinal = this.robotScale * 0.001;
      this.robot.scaleX = robotScaleFinal;
      this.robot.scaleY = robotScaleFinal;
      this.robot.x += (550 - this.robot.x) * 0.0011;
      this.robot.y += (180 - this.robot.y) * 0.0011;
    }
    if (this.robotOn === 2) {
      if (this.robotSpeed < 90) {
        ++this.robotSpeed;
      }
      this.robot.x += this.robotSpeed * 0.1;
    }
    if (this.robotOn >= 1) {
      this.background1.x -= 1.5;
      if (this.background1.x < -1000) {
        this.background1.x += 512;
      }
    }
  }

  /** Drops the finished step and applies the next one's text and action. */
  private nextStep(returnAction: CutSceneSignal): CutSceneSignal {
    this.ev.splice(0, 1);
    this.frame = 0;
    const step = this.ev[0];
    if (step.txt !== "") {
      this.clearText();
      this.txtArray = step.txt.split("");
    }
    switch (step.action) {
      case CutSceneStepAction.ClearText:
        this.clearText();
        break;
      case CutSceneStepAction.StartStorm:
        this.screenFlashOn = true;
        this.bg2On = true;
        this.rainOn = true;
        break;
      case CutSceneStepAction.HideCover:
        this.coverOn = false;
        break;
      case CutSceneStepAction.EndStormAndShowCover:
        this.coverOn = true;
        this.screenFlashOn = false;
        this.crensonOn = false;
        this.rain!.kill();
        break;
      case CutSceneStepAction.AddGeneratorEffect:
        this.addEffect(new Effect(EffectId.CutSceneCore, 360, 190));
        break;
      case CutSceneStepAction.KillEffects:
        this.effectCycle(1);
        break;
      case CutSceneStepAction.AddCoreEffect:
        this.addEffect(new Effect(EffectId.CutSceneGenerator, 360, 185));
        break;
      case CutSceneStepAction.BuildRobotBlueprint:
        this.buildRobotBlueprint();
        return CutSceneSignal.BlueprintBuilt;
      case CutSceneStepAction.LaunchBlueprintEffect:
        this.effects[0].updateCutScene(1);
        break;
      case CutSceneStepAction.ShowSecondObject:
        this.obj2On = true;
        break;
      case CutSceneStepAction.HideObjects:
        this.clearText();
        this.effectCycle(1);
        this.obj2On = false;
        this.obj1On = false;
        this.coverOn = true;
        break;
      case CutSceneStepAction.ShowFortress:
        this.fortressOn = true;
        return CutSceneSignal.FortressAppeared;
      case CutSceneStepAction.SwitchToTunnel:
        this.switchToTunnel();
        break;
      case CutSceneStepAction.LaunchRobot:
        this.launchRobot();
        return CutSceneSignal.RobotLaunched;
      case CutSceneStepAction.SwitchToCircuit:
        this.switchToCircuit();
        break;
      case CutSceneStepAction.AddRobotShine:
        this.addRobotShine();
        return CutSceneSignal.RobotShine;
      case CutSceneStepAction.HideEndingCover:
        this.coverOn = false;
        break;
      case CutSceneStepAction.DestroyFortress:
        this.destroyFortress();
        return CutSceneSignal.FortressDestroyed;
      case CutSceneStepAction.ShowEndingCover:
        this.coverOn = true;
        break;
      case CutSceneStepAction.AddFinalBossEffect:
        this.addEffect(new Effect(EffectId.FinalBossCutScene, 360, 180));
        this.obj1On = false;
        this.obj1!.visible = false;
        break;
      case CutSceneStepAction.KillEndingEffects:
        this.effectCycle(1);
        break;
      case CutSceneStepAction.SwitchToSunset:
        this.switchToSunset();
        break;
      case CutSceneStepAction.SetCyanText:
        this.clearText();
        this.format.color = TEXT_COLOURS.cyan;
        this.txt.defaultTextFormat = this.format;
        break;
      case CutSceneStepAction.ClearEndingText:
        this.clearText();
        break;
      case CutSceneStepAction.StartRobotFlyaway:
        this.robotOn = 2;
        break;
      case CutSceneStepAction.FinishEnding:
        return CutSceneSignal.EndingFinished;
      case CutSceneStepAction.StartEndingMusic:
        return CutSceneSignal.StartEndingMusic;
      case CutSceneStepAction.RevealCrenson:
        this.crensonOn = true;
        this.crenson.alpha = 0;
        this.crenson.visible = true;
        this.fortressOn = false;
        break;
      case CutSceneStepAction.FinishScene:
        this.txt.visible = false;
        return CutSceneSignal.SceneFinished;
    }
    return returnAction;
  }

  /** Opening action 8: the robot blueprint fades in over the circuit board. */
  private buildRobotBlueprint(): void {
    this.obj2 = new MovieClip();
    this.addChild(this.obj2);
    const obj2Spr = new Bitmap(new G_robotInner());
    obj2Spr.smoothing = true;
    this.obj2.addChild(obj2Spr);
    this.obj2.alpha = 0;
    this.obj1 = new MovieClip();
    this.addChild(this.obj1);
    const obj1Spr = new Bitmap(new G_robot());
    obj1Spr.smoothing = true;
    this.obj1.addChild(obj1Spr);
    this.obj1.alpha = 0;
    this.obj1On = true;
    this.obj1.x = 355;
    this.obj1.y = 5;
    this.obj1.scaleX = 1.5;
    this.obj1.scaleY = 1.5;
    this.obj2.x = 355;
    this.obj2.y = 5;
    this.obj2.scaleX = 1.5;
    this.obj2.scaleY = 1.5;
    this.addChild(this.sceneCover);
    this.addChild(this.tBox);
    this.addChild(this.txt);
    this.addChild(this.spaceTxt);
  }

  /** Opening action 13: swap the plains for the tunnel and scroll it fast. */
  private switchToTunnel(): void {
    this.background1.removeChildAt(0);
    this.background2.visible = false;
    fillLayer(this.background1, new G_openingTunnel(), 2000, 480);
    this.background1.x = 0;
    this.background1.y = 0;
    this.background1Scroll = -10;
    this.coverOn = false;
  }

  /** Opening action 14: the robot dashes in down the tunnel. */
  private launchRobot(): void {
    this.robot = new MovieClip();
    this.robot.x = -200;
    this.robot.y = 180;
    this.addChild(this.robot);
    for (let trailIndex = 0; trailIndex < 6; trailIndex++) {
      this.addRobotEffect(
        new Effect(EffectId.RobotTrail, -500, -500, trailIndex, 0),
      );
    }
    this.robot.addChild(
      centred(this.hyperArmour ? new pPoseArmour1() : new pPose1()),
    );
    this.addRobotEffect(new Effect(EffectId.RobotDash, 0, 0, 0, 0));
    this.robotOn = 1;
    this.robotSpeed = 22;
    this.screenFlashAlpha = 110;
  }

  /** Opening action 15: swap both layers for the circuit-board backdrop. */
  private switchToCircuit(): void {
    this.background1.removeChildAt(0);
    fillLayer(this.background1, new G_circuitBack(), 2000, 480);
    this.background1.y = 0;
    this.background2Scroll = 0;
    this.background2.x = 0;
    this.background2.removeChildAt(0);
    this.background2.addChild(new Bitmap(new G_bgCircuit()));
    this.coverOn = false;
  }

  /** Opening action 16: the glint on the finished suit. */
  private addRobotShine(): void {
    this.addRobotEffect(
      this.hyperArmour
        ? new Effect(EffectId.CutSceneShine, 283 - 191, 65 - 114)
        : new Effect(EffectId.CutSceneShine, 284 - 191, 57 - 110),
    );
  }

  /** Ending action 30: the fortress blows up behind Asterus. */
  private destroyFortress(): void {
    this.screenFlashAlpha = 115;
    this.fortress.visible = false;
    this.explodeOn = false;
    this.robotShadow = 115;
    this.clearText();
    this.obj1On = true;
    this.obj1!.visible = true;
  }

  /** Ending action 36: sunset backdrop and a fresh robot flying across it. */
  private switchToSunset(): void {
    this.clearText();
    this.removeChild(this.background1);
    this.background1 = new MovieClip();
    this.addChild(this.background1);
    fillLayer(this.background1, new BG_endingSunset(), 2000, 480);
    this.background1.x = -10;
    this.background1.y = -45;
    this.removeChild(this.robot);
    this.robotOn = 1;
    this.robot = new MovieClip();
    this.robot.x = 50;
    this.robot.y = 210;
    this.robot.scaleX = 1.35;
    this.robot.scaleY = this.robot.scaleX;
    this.addChild(this.robot);
    for (let trailIndex = 0; trailIndex < 6; trailIndex++) {
      this.addRobotEffect(
        new Effect(EffectId.RobotTrail, -500, -500, trailIndex, -1),
      );
    }
    this.robot.addChild(
      centred(this.hyperArmour ? new pPoseArmourSunset2() : new pPoseSunset2()),
    );
    this.addRobotEffect(new Effect(EffectId.RobotDash, 0, 0, 0, 3));
    this.addChild(this.sceneCover);
    this.addChild(this.tBox);
    this.addChild(this.txt);
    this.addChild(this.spaceTxt);
    this.coverOn = false;
  }

  private addEffect(eff: Effect): void {
    this.addChild(eff);
    this.effects.push(eff);
  }

  private addRobotEffect(eff: Effect): void {
    this.robot.addChild(eff);
    this.effects.push(eff);
  }

  private clearText(): void {
    this.txtArray = [];
    this.txt.text = "";
    this.txtCurrent = "";
  }

  private effectCycle(mode = 0): void {
    for (
      let effectIndex = 0;
      effectIndex < this.effects.length;
      effectIndex++
    ) {
      const eff = this.effects[effectIndex];
      if (mode === 1) {
        eff.kill();
        continue;
      }
      eff.updateCutScene(0);
      if (eff.id === EffectId.RobotTrail) {
        eff.updateTrail(0, 0);
      }
      if (eff.updateAlpha() === 1) {
        // trail and dash effects are children of the robot, everything else of the scene
        if (eff.id < EffectId.RobotTrail) {
          this.removeChild(eff);
        } else {
          this.robot.removeChild(eff);
        }
        this.effects.splice(effectIndex, 1);
        effectIndex--;
      }
    }
  }

  private addText(): void {
    if (this.txtArray.length > 0) {
      if (this.txtArray[0] === "$") {
        this.txtArray[0] = "\n";
      }
      this.txtCurrent += this.txtArray[0];
      this.txtArray.splice(0, 1);
      this.txt.text = this.txtCurrent;
    }
  }
}

/** the scene's background idiom: a bitmap-filled rectangle inside `parent`. */
function fillLayer(
  parent: MovieClip,
  bitmap: BitmapData,
  w: number,
  h: number,
): void {
  const spr = new Sprite();
  spr.graphics.beginBitmapFill(bitmap);
  spr.graphics.drawRect(0, 0, w, h);
  spr.graphics.endFill();
  parent.addChild(spr);
}

/** a bitmap shifted so its middle sits on its parent's origin. */
function centred(bitmap: BitmapData): Bitmap {
  const spr = new Bitmap(bitmap);
  spr.x = spr.width * -0.5;
  spr.y = spr.height * -0.5;
  return spr;
}
